import { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { useEnvironmentOpsService } from "@/hooks/use-environment-ops-service";
import type { ReviewComment, ReviewCommentAuthor, CachedCodeLine, CommentSide } from "@/components/ai/review/types";
import { createReviewComment } from "@/components/ai/review/types";
import { WriteMode } from "gitpod-next-api/supervisor/v1/environmentopts_pb";

type ReviewCommentsStorage = {
    version: 1;
    comments: ReviewComment[];
    dismissedAnnotations: string[];
};

// Storage path is relative to workspace folder (API resolves relative paths)
// Files in .ona/review/ are excluded from git via .git/info/exclude
const STORAGE_FILE_PATH = ".ona/review/comments.json";

const defaultStorage: ReviewCommentsStorage = {
    version: 1,
    comments: [],
    dismissedAnnotations: [],
};

/**
 * Hook for managing review comments for a specific environment using file-based storage.
 * Comments are stored in .ona/review/comments-<envId>.json relative to the workspace folder.
 */
export const useFileBasedReviewComments = (environmentId: string) => {
    const client = useEnvironmentOpsService(environmentId);
    const [storage, setStorage] = useState<ReviewCommentsStorage>(defaultStorage);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Ref to track dismissed annotations synchronously (avoids race conditions)
    const dismissedAnnotationsRef = useRef<Set<string>>(new Set());

    // Track whether initial load is complete to avoid saving on mount
    const isInitialLoadComplete = useRef(false);

    // Load comments from file on mount
    useEffect(() => {
        if (!client) {
            setIsLoading(false);
            return;
        }

        const loadComments = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await client.readFile({ path: STORAGE_FILE_PATH });
                if (response.result.case === "content") {
                    const content = new TextDecoder().decode(response.result.value.data);
                    const parsed = JSON.parse(content) as ReviewCommentsStorage;
                    setStorage(parsed);
                    dismissedAnnotationsRef.current = new Set(parsed.dismissedAnnotations);
                }
            } catch (err) {
                // File not found is expected for new environments
                if (err instanceof Error && err.message.includes("not found")) {
                    setStorage(defaultStorage);
                } else {
                    setError(err instanceof Error ? err : new Error("Failed to load comments"));
                }
            } finally {
                setIsLoading(false);
                isInitialLoadComplete.current = true;
            }
        };

        void loadComments();
    }, [client, environmentId]);

    // Persist storage changes to file
    useEffect(() => {
        // Skip saving during initial load
        if (!isInitialLoadComplete.current || !client) {
            return;
        }

        const saveStorage = async () => {
            try {
                const content = JSON.stringify(storage, null, 2);
                await client.writeFile({
                    path: STORAGE_FILE_PATH,
                    content: new TextEncoder().encode(content),
                    mode: WriteMode.CREATE_OR_TRUNCATE,
                });
                setError(null);
            } catch (err) {
                console.error("Failed to save review comments:", err);
                setError(err instanceof Error ? err : new Error("Failed to save comments"));
            }
        };

        void saveStorage();
    }, [storage, client, environmentId]);

    const addComment = useCallback(
        (
            filePath: string,
            startLine: number,
            endLine: number,
            content: string,
            author: ReviewCommentAuthor = "user",
            annotationId?: string,
            side?: CommentSide,
            contentHash?: string,
        ) => {
            const comment = createReviewComment(
                filePath,
                startLine,
                endLine,
                content,
                author,
                annotationId,
                side,
                contentHash,
            );
            setStorage((prev) => ({
                ...prev,
                comments: [...prev.comments, comment],
            }));
            // When a comment is added, show the review tab
            setIsDismissed(false);
            return comment;
        },
        [],
    );

    const updateComment = useCallback((commentId: string, content: string) => {
        setStorage((prev) => ({
            ...prev,
            comments: prev.comments.map((c) => (c.id === commentId ? { ...c, content, updatedAt: Date.now() } : c)),
        }));
    }, []);

    const setCommentThumbsDown = useCallback((commentId: string, thumbsDown: boolean) => {
        setStorage((prev) => ({
            ...prev,
            comments: prev.comments.map((c) =>
                c.id === commentId ? { ...c, thumbsDown, updatedAt: Date.now() } : c,
            ),
        }));
    }, []);

    const removeComment = useCallback((commentId: string) => {
        setStorage((prev) => {
            const comment = prev.comments.find((c) => c.id === commentId);
            let newDismissedAnnotations = prev.dismissedAnnotations;

            if (comment && comment.author === "agent" && comment.annotationId) {
                // Update ref immediately to prevent race condition
                dismissedAnnotationsRef.current.add(comment.annotationId);
                // Add to dismissed annotations if not already there
                if (!prev.dismissedAnnotations.includes(comment.annotationId)) {
                    newDismissedAnnotations = [...prev.dismissedAnnotations, comment.annotationId];
                }
            }

            return {
                ...prev,
                comments: prev.comments.filter((c) => c.id !== commentId),
                dismissedAnnotations: newDismissedAnnotations,
            };
        });
    }, []);

    const isAnnotationDismissed = useCallback((annotationId: string): boolean => {
        // Check ref for immediate updates (avoids race condition)
        return dismissedAnnotationsRef.current.has(annotationId);
    }, []);

    const clearAllComments = useCallback(() => {
        setStorage((prev) => {
            // Mark all agent comments as dismissed before clearing
            const agentAnnotationIds = prev.comments
                .filter((c) => c.author === "agent" && c.annotationId)
                .map((c) => c.annotationId!);

            // Update ref immediately
            for (const id of agentAnnotationIds) {
                dismissedAnnotationsRef.current.add(id);
            }

            const uniqueNewIds = agentAnnotationIds.filter((id) => !prev.dismissedAnnotations.includes(id));

            return {
                ...prev,
                comments: [],
                dismissedAnnotations:
                    uniqueNewIds.length > 0
                        ? [...prev.dismissedAnnotations, ...uniqueNewIds]
                        : prev.dismissedAnnotations,
            };
        });
    }, []);

    const updateCodeContext = useCallback(
        (commentId: string, contentHash: string, cachedCodeLines: CachedCodeLine[]) => {
            setStorage((prev) => ({
                ...prev,
                comments: prev.comments.map((c) =>
                    c.id === commentId && !c.contentHash ? { ...c, contentHash, cachedCodeLines } : c,
                ),
            }));
        },
        [],
    );

    const dismissReviewTab = useCallback(() => {
        setIsDismissed(true);
    }, []);

    const showReviewTab = useCallback(() => {
        setIsDismissed(false);
    }, []);

    const comments = storage.comments;
    const hasComments = comments.length > 0;
    const isReviewTabVisible = hasComments && !isDismissed;

    const commentsByFile = useMemo(() => {
        return Object.groupBy(comments, (comment) => comment.filePath);
    }, [comments]);

    const getCommentsForFile = useCallback(
        (filePath: string) => {
            return comments.filter((c) => c.filePath === filePath);
        },
        [comments],
    );

    const getCommentsForLineRange = useCallback(
        (filePath: string, startLine: number, endLine: number) => {
            return comments.filter(
                (c) =>
                    c.filePath === filePath &&
                    // Check if the comment's line range overlaps with the given range
                    c.startLine <= endLine &&
                    c.endLine >= startLine,
            );
        },
        [comments],
    );

    return {
        comments,
        commentsByFile,
        hasComments,
        isReviewTabVisible,
        isLoading,
        error,
        isServiceAvailable: !!client,
        addComment,
        updateComment,
        removeComment,
        clearAllComments,
        dismissReviewTab,
        showReviewTab,
        getCommentsForFile,
        getCommentsForLineRange,
        isAnnotationDismissed,
        updateCodeContext,
        setCommentThumbsDown,
    };
};
